import React, {
  useContext, useMemo, useState,
} from 'react';

import Config from '@app/dev/config/main';
import { POPUPS } from '@common/constants/popups';
import { PopupContext } from '@app/dev/contexts/popup';
import TooltipDropDown from '@common/components/tooltipDropDown/index';
import SimpleDropDown from '@common/components/simpleDropdown/index';
import { PreferencesContext } from '@common/contexts/preferences';
import { useTranslation } from '@common/hooks/translation';
import Helpers from '@common/helpers/helperFunctions';
import { ascSort } from '@common/helpers/declarative/sortings';

import './style.scss';
import { UIContext } from '@app/dev/contexts/ui';
import { checkTerminalMode } from '@common/helpers';
import { SetLanguage } from '@app/dev/actions/terminal';
import { changeDateLocale } from '@common/helpers/dateLocale';
import { useDispatch } from 'react-redux';
import { Swarm } from '@lbs/swarm-client';

const SettingSection = () => {
  const [{ openPopup }] = useContext(PopupContext);
  const [openedItemId, setOpenedItemId] = useState(null);
  const [preferences, setPreferences] = useContext(PreferencesContext);
  const [uiState, setUIState] = useContext(UIContext);
  const dispatch = useDispatch();
  const t = useTranslation();

  const changeLanguage = ({ value }) => {
    const lang = Config.swarm.languageMap[value] || value;

    if (checkTerminalMode(Config, 'betting')) {
      Swarm.getInstance().changeLanguage(lang);
    }

    setPreferences({ ...preferences, lang: value });
    dispatch(SetLanguage(lang));
    changeDateLocale(lang);
  };

  const availableOddsOptions = Config.availableOdds
    .filter((item) => item.enabled)
    .map((item) => ({ label: t(item.title), value: item.odds }))

  const menuItems = useMemo(
    () => [
      {
        id: 1,
        label: 'Admin Access',
        section: 'adminAccess',
        icon: 'global-icon icon-bold-avatar',
        onClick: () => openPopup(POPUPS.LOGIN_FORM, { signInToAdmin: true, popupPosition: 'top' }),
      },
      availableOddsOptions.length > 1 && {
        id: 2,
        label: 'Odds Type',
        section: 'oddsType',
        value: preferences.oddsFormat,
        icon: 'global-icon icon-odds-type',
        onClick: ({ value }) => setPreferences({
          ...preferences,
          oddsFormat: value,
        }),
        options: availableOddsOptions,
      },
      Object.values(Config.main.availableLanguages).length > 1 && {
        id: 3,
        label: 'Language',
        displayName: Config.main.availableLanguages[preferences.lang]?.name,
        value: preferences.lang,
        section: 'language',
        icon: 'global-icon icon-language',
        onClick: changeLanguage,
        options: Helpers.objectToArray(Config.main.availableLanguages, 'code')
          .sort(ascSort('order'))
          .map((item) => ({
            label: item.name,
            value: item.code,
          })),
      },
      {
        id: 4,
        label: 'System View',
        section: 'systemView',
        value: uiState.theme,
        icon: 'global-icon icon-mode',
        onClick: ({ value }) => setUIState({
          ...uiState,
          theme: value,
        }),
        options: [
          {
            label: 'light',
            value: 'light',
          },
          {
            label: 'dark',
            value: 'dark',
          },
        ],
      },
      {
        id: 5,
        label: 'Privacy Notice',
        section: 'privacyNotice',
        icon: 'global-icon icon-help1',
        onClick: () => openPopup(POPUPS.TERMS_AND_CONDITIONS),
      },
    ].filter(Boolean),
    [preferences, uiState, changeLanguage, setPreferences, t, openPopup, setUIState],
  );

  const onMenuItemClick = (item) => {
    if (item.options) setOpenedItemId(openedItemId === item.id ? null : item.id);
    else item.onClick();
  };

  const onClose = () => setOpenedItemId(null);

  if (!Config.settingsSection.enabled) return null;

  return (
    <SimpleDropDown
      header={
        <i className="settings-menu-icon global-icon icon-Settings" />
      }
      onClose={onClose}
      className="setting-section"
    >
      <div className="menu-wrapper">
        {menuItems.map((item) => (
          Config.settingsSection?.sections.includes(item.section) && (
            <div
              key={item.id}
              className={`menu-item-wrapper ${openedItemId === item.id ? 'opened' : ''
                }`}
            >
              <div
                key={item.id}
                className="menu-item"
                onClick={() => onMenuItemClick(item)}
              >
                <div className="left-content">
                  <i className={`setting-icons ${item.icon}`} />
                  <span>{t(item.label)}</span>
                </div>
                <div className="right-content">
                  <div className="item-value">{t(item.displayName || item.value)}</div>
                  <i
                    className={`global-icon icon-arrow-${item.options ? openedItemId === item.id ? 'up4' : 'down5' : 'right5'
                      }`}
                  />
                </div>
              </div>
              {openedItemId === item.id && (
                <div className="options-list">
                  {item.options.map((option) => (
                    <div
                      key={option.label}
                      className={`option-item ${item.value === option.value ? 'active' : ''
                        }`}
                      onClick={() => item.onClick(option)}
                    >
                      {t(option.label)}
                      {
                        item.value === option.value && <i className="global-icon icon-regular-checkmark" />
                      }
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        ))}
      </div>
    </SimpleDropDown>
  );

  return (
    <TooltipDropDown
      header={
        <i className={`global-icon icon-${Config.settingsSection.icon}`} />
      }
      options={sectionData}
      className="setting-section"
    />
  );
};

export default SettingSection;
